import { Link } from '@inertiajs/react'
import dayjs from 'dayjs'

import { Stat } from '@atoms/Stat'
import Icon from '@components/utils/Icon'
import { BlastRowActions, type BlastAction } from '@components/molecules/BlastRowActions'
import { TruncationTooltip } from '@components/molecules/TruncationTooltip'
import { BlastStatusBadge } from '@components/atoms/BlastStatusBadge'
import { scopedTranslation } from '@utils/I18n'

import type { Blast, BlastStatus } from '../../types'
import { groupUrl } from '../../utils/routes'

const t = scopedTranslation('comms_management.blasts_index')

function GroupStat({ blast }: { blast: Blast }) {
  if (!blast.actionGroup) return null

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <dt className="tw:inline-flex tw:items-end tw:gap-1.5 tw:text-xs tw:font-bold tw:uppercase tw:leading-[18px] tw:text-gray-true-500">
        <Icon type="users-01" width={20} height={20} className="tw:shrink-0 tw:text-gray-true-500" aria-hidden />
        {t('columns.group')}
      </dt>
      <dd className="tw:m-0 tw:min-w-0 tw:text-sm">
        <TruncationTooltip content={blast.actionGroup.name}>
          <Link
            href={groupUrl(blast.actionGroup.id)}
            className="tw:inline-block tw:max-w-full tw:truncate tw:align-bottom tw:border-b tw:border-transparent tw:font-medium tw:text-primary-700 tw:hover:border-current tw:hover:text-primary-900"
          >
            {blast.actionGroup.name}
          </Link>
        </TruncationTooltip>
      </dd>
    </div>
  )
}

export function BlastCard({
  blast,
  action,
  audienceLabel,
  statusDetail,
}: {
  blast: Blast
  action: BlastAction
  audienceLabel: string
  statusDetail?: string
}) {
  const title = blast.title ?? ''

  return (
    <li className="tw:flex tw:w-full tw:flex-col tw:gap-5 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-4 tw:shadow-xs tw:sm:p-6">
      <div className="tw:flex tw:flex-wrap tw:items-start tw:gap-x-6 tw:gap-y-2">
        <div className="tw:w-full tw:min-w-0 tw:sm:w-auto tw:sm:flex-1">
          <TruncationTooltip content={title} contentClassName="tw:max-w-md">
            <h2 className="tw:m-0 tw:truncate tw:text-lg tw:font-semibold tw:text-gray-900">
              <a href={action.blastUrl} className="tw:hover:text-primary-700">
                {title}
              </a>
            </h2>
          </TruncationTooltip>

          <p className="tw:m-0 tw:mt-1 tw:text-sm tw:text-gray-true-500">
            {t('created_byline', { date: dayjs(blast.createdAt).format('D MMM YYYY') })}
          </p>
        </div>

        <BlastStatusBadge status={blast.status as BlastStatus} detail={statusDetail} />
      </div>

      <dl className="tw:m-0 tw:grid tw:grid-cols-2 tw:gap-x-6 tw:gap-y-4 tw:sm:grid-cols-3">
        <GroupStat blast={blast} />

        <Stat icon="filter-lines" label={t('columns.audience')} value={audienceLabel} />

        <Stat icon="users-01" label={t('columns.recipients')} value={blast.recipientCount.toLocaleString()} />
      </dl>

      <div className="tw:w-full tw:border-t tw:border-gray-200" />

      <div className="tw:flex tw:flex-wrap tw:gap-3 tw:sm:justify-between">
        <a
          href={action.blastUrl}
          className="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-primary-600 tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:text-white tw:hover:bg-primary-700"
        >
          <Icon type="eye" width={16} height={16} aria-hidden />
          {t('actions.view')}
        </a>

        <span className="tw:inline-flex tw:flex-wrap tw:gap-3">
          <BlastRowActions action={action} />
        </span>
      </div>
    </li>
  )
}
